import { useState, useEffect } from "react";
import { OPTOMETRIST_QUESTIONS, DISPENSER_QUESTIONS } from "../data/questions";
import ScorecardForm from "../components/ScorecardForm";
import Login from "./Login";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

function Scorecard() {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("portalUser");
    return raw ? JSON.parse(raw) : null;
  });

  const [applicants, setApplicants] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // selected applicant evaluation
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [testData, setTestData] = useState(null);     // { test: { ...answers } }
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState("");

  // marking
  const [marks, setMarks] = useState({});             // { answer_id: { is_correct, mark_reason } }
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState("");
  const [markSuccess, setMarkSuccess] = useState("");

  // active tab
  const [tab, setTab] = useState("test");

  const authToken = () => localStorage.getItem("portalToken");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (user) loadApplicants();
    // eslint-disable-next-line
  }, []);

  const loadApplicants = async () => {
    try {
      setLoadingList(true);
      setListError("");
      const res = await fetch(`${API_BASE}/api/admin/applicants`, {
        headers: { Authorization: `Bearer ${authToken()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not load applicants");
      const list = Array.isArray(d.applicants) ? d.applicants : [];
      // show only applicants that have a test
      setApplicants(list.filter((a) => a.latest_test));
    } catch (err) {
      setListError(err.message || "Could not load applicants");
    } finally {
      setLoadingList(false);
    }
  };

  const selectApplicant = async (applicant) => {
    setSelectedApplicant(applicant);
    setTestData(null);
    setTestError("");
    setMarks({});
    setMarkError("");
    setMarkSuccess("");
    setTab("test");
    try {
      setTestLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/test`, {
        headers: { Authorization: `Bearer ${authToken()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not load test");
      setTestData(d.test);
      // pre-populate marks from existing data
      if (d.test?.answers) {
        const m = {};
        d.test.answers.forEach((a) => {
          m[a.id] = { is_correct: a.is_correct, mark_reason: a.mark_reason || "" };
        });
        setMarks(m);
      }
    } catch (err) {
      setTestError(err.message || "Could not load test");
    } finally {
      setTestLoading(false);
    }
  };

  const handleMarkSave = async () => {
    if (!testData) return;
    try {
      setMarking(true);
      setMarkError("");
      setMarkSuccess("");
      const payload = Object.entries(marks).map(([id, m]) => ({
        answer_id: Number(id),
        is_correct: m.is_correct,
        mark_reason: m.mark_reason || "",
      }));
      const res = await fetch(`${API_BASE}/api/admin/applicants/${selectedApplicant.id}/test/mark`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ marks: payload }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not save marks");
      setTestData(d.test);
      setMarkSuccess("Marks saved successfully.");
      // refresh list to update score display
      loadApplicants();
    } catch (err) {
      setMarkError(err.message || "Could not save marks");
    } finally {
      setMarking(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    loadApplicants();
  };

  if (!user) return <Login onLoginSuccess={handleLoginSuccess} />;

  const filteredApplicants = applicants.filter((a) => {
    const name = `${a.user?.first_name || ""} ${a.user?.last_name || ""}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase()) || (a.folder_number || "").toLowerCase().includes(searchTerm.toLowerCase());
  });

  const questions = testData ? (testData.role === "optometrist" ? OPTOMETRIST_QUESTIONS : DISPENSER_QUESTIONS) : [];

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      style={{
        padding: "8px 20px",
        border: "none",
        borderBottom: tab === key ? "3px solid #8b3db8" : "3px solid transparent",
        background: "transparent",
        cursor: "pointer",
        fontWeight: tab === key ? 700 : 400,
        color: tab === key ? "#8b3db8" : "#555",
        fontSize: 14,
      }}
    >{label}</button>
  );

  const inputStyle = { padding: "6px 8px", borderRadius: 4, border: "1px solid #ccc", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 960, display: "grid", gridTemplateColumns: "280px 1fr", gap: 16, alignItems: "start" }}>

      {/* ── left: applicant list ── */}
      <div style={{ border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "12px 14px", background: "#8b3db8", color: "white" }}>
          <strong style={{ fontSize: 14 }}>Applicants with Tests</strong>
        </div>
        <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or folder…"
            style={{ ...inputStyle, width: "100%" }}
          />
        </div>
        {listError && <p style={{ padding: "8px 10px", color: "#b3261e", fontSize: 12, margin: 0 }}>{listError}</p>}
        {loadingList && <p style={{ padding: "8px 10px", color: "#777", fontSize: 12, margin: 0 }}>Loading…</p>}
        {!loadingList && filteredApplicants.length === 0 && (
          <p style={{ padding: "8px 10px", color: "#999", fontSize: 12, margin: 0 }}>No applicants with tests found.</p>
        )}
        {filteredApplicants.map((a) => {
          const isSelected = selectedApplicant?.id === a.id;
          const test = a.latest_test;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => selectApplicant(a)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "10px 14px",
                border: "none",
                borderBottom: "1px solid #f0f0f0",
                background: isSelected ? "#ede7f6" : "white",
                cursor: "pointer",
                borderLeft: isSelected ? "3px solid #8b3db8" : "3px solid transparent",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {a.user?.first_name} {a.user?.last_name}
              </div>
              <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
                {a.folder_number || "No folder"} · {a.position_applied || "—"}
              </div>
              {test && (
                <div style={{ marginTop: 4, fontSize: 11 }}>
                  <span style={{
                    padding: "1px 7px",
                    borderRadius: 10,
                    background: test.status === "marked" ? "#e8f5e9" : test.status === "submitted" ? "#fff3e0" : "#e3f2fd",
                    color: test.status === "marked" ? "#2e7d32" : test.status === "submitted" ? "#e65100" : "#1565c0",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}>
                    {test.status}
                  </span>
                  {test.score != null && (
                    <span style={{ marginLeft: 6, fontWeight: 700, color: test.score >= 60 ? "#2e7d32" : "#b71c1c" }}>{test.score}%</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ── right: evaluation panel ── */}
      <div>
        {!selectedApplicant && (
          <div style={{ padding: 40, textAlign: "center", color: "#aaa", border: "1px dashed #ddd", borderRadius: 8 }}>
            Select an applicant on the left to view and mark their assessment.
          </div>
        )}

        {selectedApplicant && (
          <div>
            {/* header */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: "0 0 4px", color: "#8b3db8" }}>
                {selectedApplicant.user?.first_name} {selectedApplicant.user?.last_name}
              </h4>
              <div style={{ fontSize: 13, color: "#555" }}>
                {selectedApplicant.position_applied || "—"} · {selectedApplicant.city || ""} {selectedApplicant.province || ""}
              </div>
            </div>

            {/* tabs */}
            <div style={{ borderBottom: "1px solid #e0e0e0", marginBottom: 16, display: "flex" }}>
              {tabBtn("test", "Test Answers & Marking")}
              {tabBtn("scorecard", "Scorecard")}
            </div>

            {/* ── test tab ── */}
            {tab === "test" && (
              <div>
                {testLoading && <p style={{ color: "#777" }}>Loading test…</p>}
                {testError && <p style={{ color: "#b3261e" }}>{testError}</p>}
                {!testLoading && !testData && !testError && (
                  <p style={{ color: "#999" }}>No test data found.</p>
                )}
                {testData && (
                  <div>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16, padding: "10px 14px", background: "#f5f5f5", borderRadius: 6 }}>
                      <div style={{ fontSize: 13 }}>
                        <strong>Role:</strong> <span style={{ textTransform: "capitalize" }}>{testData.role}</span>
                      </div>
                      <div style={{ fontSize: 13 }}>
                        <strong>Status:</strong> <span style={{ textTransform: "capitalize" }}>{testData.status}</span>
                      </div>
                      {testData.score != null && (
                        <div style={{ fontSize: 13 }}>
                          <strong>Score:</strong>{" "}
                          <span style={{ fontWeight: 700, color: testData.score >= 60 ? "#2e7d32" : "#b71c1c" }}>{testData.score}%</span>
                        </div>
                      )}
                      {testData.submitted_at && (
                        <div style={{ fontSize: 13, color: "#777" }}>
                          Submitted: {new Date(testData.submitted_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    {(testData.answers || []).map((answer) => {
                      const q = questions.find((x) => x.index === answer.question_index);
                      const m = marks[answer.id] || { is_correct: null, mark_reason: "" };
                      return (
                        <div key={answer.id} style={{ border: "1px solid #e0e0e0", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                          <div style={{ padding: "10px 14px", background: "#f9f9f9", borderBottom: "1px solid #eee" }}>
                            <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>Question {answer.question_index + 1}</div>
                            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.5 }}>{q?.question || `Question ${answer.question_index + 1}`}</div>
                          </div>
                          <div style={{ padding: "10px 14px" }}>
                            <div style={{ fontSize: 13, color: "#333", marginBottom: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", background: "#fbfbfb", padding: "8px 10px", borderRadius: 4, border: "1px solid #eeeeee" }}>
                              {answer.answer_text || <em style={{ color: "#aaa" }}>No answer provided.</em>}
                            </div>

                            {isAdmin && (
                              <div>
                                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                                  <button
                                    type="button"
                                    onClick={() => setMarks((prev) => ({ ...prev, [answer.id]: { ...m, is_correct: true } }))}
                                    style={{ padding: "5px 14px", borderRadius: 5, border: `2px solid ${m.is_correct === true ? "#2e7d32" : "#e0e0e0"}`, background: m.is_correct === true ? "#e8f5e9" : "white", color: m.is_correct === true ? "#2e7d32" : "#555", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                                  >✓ Correct</button>
                                  <button
                                    type="button"
                                    onClick={() => setMarks((prev) => ({ ...prev, [answer.id]: { ...m, is_correct: false } }))}
                                    style={{ padding: "5px 14px", borderRadius: 5, border: `2px solid ${m.is_correct === false ? "#c62828" : "#e0e0e0"}`, background: m.is_correct === false ? "#ffebee" : "white", color: m.is_correct === false ? "#c62828" : "#555", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                                  >✗ Incorrect</button>
                                </div>
                                <input
                                  value={m.mark_reason || ""}
                                  onChange={(e) => setMarks((prev) => ({ ...prev, [answer.id]: { ...m, mark_reason: e.target.value } }))}
                                  placeholder="Optional: reason / feedback…"
                                  style={{ ...inputStyle, width: "100%", fontSize: 12 }}
                                />
                              </div>
                            )}

                            {!isAdmin && answer.is_correct != null && (
                              <div style={{ fontSize: 12, fontWeight: 600, color: answer.is_correct ? "#2e7d32" : "#c62828" }}>
                                {answer.is_correct ? "✓ Correct" : "✗ Incorrect"}
                                {answer.mark_reason && <span style={{ fontWeight: 400, color: "#555", marginLeft: 8 }}>{answer.mark_reason}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {isAdmin && (testData.answers || []).length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        {markError && <p style={{ color: "#b3261e", margin: "0 0 8px" }}>{markError}</p>}
                        {markSuccess && <p style={{ color: "#2e7d32", margin: "0 0 8px" }}>{markSuccess}</p>}
                        <button type="button" onClick={handleMarkSave} disabled={marking} style={{ padding: "10px 24px", background: "#1976d2", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                          {marking ? "Saving…" : "Save All Marks"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── scorecard tab ── */}
            {tab === "scorecard" && (
              <ScorecardForm applicantId={selectedApplicant.id} authToken={authToken()} isAdmin={isAdmin} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Scorecard;
